import BreadCrumb from '@/ui//breadCrumb/BreadCrumb';
import {
  Container,
  ContentsWrapper,
  CompanyInfoWrapper,
  CompanyNameWrapper,
  CompanyScoreWrapper,
  ChipWrapper,
  ButtonWrapper,
  MobileButtonWrapper,
  MenuWrapper,
} from './CompanyDetailMenuStyles';
import UpDownChip from '@/ui//caption/score3/Score3';
import AddButton from '@/ui//buttons/addButton/AddButton';
import PrimaryMenu from '@/ui//menus/primaryMenu/PrimaryMenu';
import { Skeleton } from '@/components/ui/skeleton';
import Tag from '@/ui//tag/Tag';
import { useTranslation } from 'react-i18next';
import useFollowCompany from '@/hooks/useFollowCompany';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getCompanyEsgProfile } from '@/api/companyDetail';
import { useEffect, useState } from 'react';
import SigninCTAModal from '@/ui//modals/signInCTAModal/SigninCTAModal';
import { cn } from '@/lib/utils';

const CompanyDetailMenu = ({ menuIdx, setMenuIdx }) => {
  const language = useSelector((state: any) => state.user.language);
  const [t] = useTranslation('companyDetails');
  const { id } = useParams();
  const followingList = useSelector((state: any) => state.mylist.followingList);
  const isFollowing = followingList.find((el) => el.corp_code === id);

  const [addFollowing, cancelFollowing] = useFollowCompany(id);
  const [profileData, setProfileData] = useState<any>({});
  const { stock_code, esg_risk, esg_risk_change_ratio } = profileData;
  const esg_risk_change_ratio_ = esg_risk_change_ratio
    ? Number(esg_risk_change_ratio.toFixed(2))
    : undefined;

  useEffect(() => {
    if (id) getProfileData();
  }, [id]);

  const getProfileData = async () => {
    const res = await getCompanyEsgProfile(id);
    if (res?.data) setProfileData(res.data);
  };

  const handleTapChange = (i) => {
    setMenuIdx(i);
  };

  const user = useSelector((state: any) => state.user.info);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Container>
      {profileData[`sics_sector_name_${language}`] ? (
        <BreadCrumb
          list={[
            t('Home'),
            profileData[`sics_sector_name_${language}`],
            profileData[`sics_industry_name_${language}`],
            profileData[`comp_name_${language}`] + ' • ' + profileData[`stock_market_${language}`],
          ]}
        />
      ) : (
        <Skeleton className="rounded-md w-[250px] h-[8px]" />
      )}

      <ContentsWrapper>
        <CompanyInfoWrapper>
          <CompanyNameWrapper>
            {profileData[`comp_name_${language}`] ? (
              <div className="flex items-center gap-2">
                <div className="text-text-500 text-xl">{profileData[`comp_name_${language}`]}</div>
                <Tag className="py-0.5 px-2">{stock_code}</Tag>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Skeleton className="rounded-md w-[48px] h-[16px]" />
                <Skeleton className="rounded-md w-[130px] h-[16px]" />
              </div>
            )}

            <MobileButtonWrapper>
              <AddButton
                isAdded={isFollowing}
                onClick={
                  Object.keys(user).length > 0
                    ? isFollowing
                      ? cancelFollowing
                      : addFollowing
                    : () => setIsOpen(true)
                }
                className={cn('h-[24px] w-[24px] mt-1 p-0 justify-center')}
              ></AddButton>
            </MobileButtonWrapper>
          </CompanyNameWrapper>
          <CompanyScoreWrapper>
            {profileData.esg_risk ? (
              <div
                className={cn(
                  'text-3xl',
                  esg_risk_change_ratio_ === 0
                    ? 'text-text-400'
                    : esg_risk_change_ratio_ < 0
                    ? 'text-legacy-blue-300'
                    : 'text-legacy-red-300',
                )}
              >
                {esg_risk.toFixed(2)}
              </div>
            ) : (
              <Skeleton className="rounded-md w-[76px] h-[38px]" />
            )}
            <ChipWrapper>
              {profileData.esg_risk_change_ratio ? (
                <UpDownChip change={esg_risk_change_ratio_} />
              ) : (
                <Skeleton className="rounded-md w-[69px] h-[24px]" />
              )}
            </ChipWrapper>
          </CompanyScoreWrapper>
        </CompanyInfoWrapper>
        <ButtonWrapper>
          <AddButton
            isAdded={isFollowing}
            onClick={
              Object.keys(user).length > 0
                ? isFollowing
                  ? cancelFollowing
                  : addFollowing
                : () => setIsOpen(true)
            }
          >
            {isFollowing ? t('Added to dashboard') : t('Add to dashboard')}
          </AddButton>
        </ButtonWrapper>
      </ContentsWrapper>

      <MenuWrapper>
        <PrimaryMenu
          activeIndex={menuIdx}
          margin="0px 60px"
          menuList={[t('Summary'), t('Timeline'), t('Benchmark'), t('News')]}
          onClick={Object.keys(user).length > 0 ? handleTapChange : () => setIsOpen(true)}
        />
      </MenuWrapper>

      <SigninCTAModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        redirection={`company/${id}`}
      />
    </Container>
  );
};

export default CompanyDetailMenu;
